/**
 * Types for the Search API response
 * Endpoint: GET /api/v2/search?query=...&take=...&skip=...
 */

import type { UmbracoLink, UmbracoImage, ProductPrice } from "@/types/layout";

export interface SearchPagination {
  take: number;
  skip: number;
}

export interface SearchSortingOption {
  name: string;
  id: string;
}

export interface SearchNavigationComposition {
  link: UmbracoLink;
  image?: UmbracoImage;
  breadcrumbs?: UmbracoLink[];
}

export interface SearchProductComposition {
  price?: ProductPrice;
  sku?: string;
  productReference?: string;
}

export interface SearchResultItemCompositions {
  navigation?: SearchNavigationComposition;
  product?: SearchProductComposition;
  productContent?: {
    sellingLine?: string;
    description?: string;
    mainCategory?: {
      name?: string;
    };
  };
}

export interface SearchResultItem {
  id: string;
  type: string;
  compositions?: SearchResultItemCompositions;
}

export interface SearchResult {
  totalCount: number;
  query: string;
  pagination: SearchPagination | null;
  activeSortingOption: SearchSortingOption | null;
  items: SearchResultItem[];
}

/**
 * Extracts the display name from a search result item.
 * Falls back through: navigation link name -> type
 */
export function getItemName(item: SearchResultItem): string {
  return item.compositions?.navigation?.link?.name ?? item.type;
}

/**
 * Extracts the navigation link from a search result item.
 *
 * Returns the whole link rather than just its `url` because an href has to be built with
 * `resolveHref`, which needs `type` to tell a content link (gets the current locale prefix) from an
 * external one (left alone). The raw `url` is whatever locale the CMS resolved server-side - often
 * Umbraco's default-culture `/global/...` - so it is never safe to use as an href directly.
 *
 * A link with a blank `url` means the page is unroutable (order confirmation emails, containers);
 * callers should skip the item rather than render a link to the locale home page.
 */
export function getItemLink(item: SearchResultItem): UmbracoLink | undefined {
  const link = item.compositions?.navigation?.link;
  return link && link.url?.trim() ? link : undefined;
}

/**
 * Extracts the image from a search result item.
 *
 * An image whose `url` is missing or blank is treated as no image at all: rendering it would put an
 * `<img src="">` in the result row, which the browser draws as a broken-image placeholder.
 */
export function getItemImage(item: SearchResultItem): UmbracoImage | undefined {
  const image = item.compositions?.navigation?.image;
  return image && image.url?.trim() ? image : undefined;
}
